/**
 * shell/useChatFollow.ts —— 流式滚动跟随控制器（UI 批 · G 组 · 20261011）
 *
 * 依据（单源）：《sononux-UI对齐清单-20261010.md》§3 G-2「已拍令（承令⑤『待拍的按 dsh 的标准来』）」：
 *   ① 状态机＝DSH 两量口径：`followingTail`（跟随意图 bool）＋ `pending`（待结算的读者移动）；
 *   ② 外层触底阈值 25px（`FOLLOW_THRESHOLD_PX` 单源；判据 `floor − top <= 25`）；
 *   ③ 采样 500ms（`SCROLL_SAMPLE_INTERVAL_MS`）＋ `scrollend`（先到者生效）；两支**即时结算**：
 *      无读者移动的贴底、读者真到 `top >= floor`（照 DSH client.js:4351-4363）；
 *   ④ 归属判据 `abs(top − min(ackTop, floor)) > 0.5`（`movedByReader`；布局增高不算读者移动）；
 *   ⑤ 锚点 `{anchorKey, anchorTop, scrollTop}` **逐会话**存（同 DSH `store.save/read` 口径）；
 *      `restore()` 先锚点行、行缺才退 raw `scrollTop`；capture 口径＝视口顶 +1px 取首个非 turn-process 锚点行；
 *   ⑥ 外层 **instant**；组内 smooth（消费位＝`TurnProcessRow` 限高容器）；
 *      「回到底部」钮仅脱离态渲染、**只恢复外层**（AppShell 消费；本件不下发按钮）。
 *   附2：`overflow-anchor` 跟随态 `none`／脱离态 `auto`——shell.css `.xg-flow[data-following]` 消费本 hook 写出的属性。
 * 纪律：本件＝全树**唯一**滚动跟随控制器实现（禁第二套）；纯函数（nearBottom／movedByReader）可单测。
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** 外层触底阈值（G-2②·DSH client.js:4460）——常量单源，禁二处。 */
export const FOLLOW_THRESHOLD_PX = 25;
/** 内层（开启且限高的过程组）阈值（G-2⑤·DSH client.js:2073）。 */
export const GROUP_THRESHOLD_PX = 1;
/** 采样周期（G-2③·DSH `SCROLL_SAMPLE_INTERVAL_MS`＝500）。 */
export const SCROLL_SAMPLE_INTERVAL_MS = 500;
/** 归属判据容差（G-2④：> 0.5px 才算读者移动）。 */
export const READER_MOVE_EPSILON_PX = 0.5;

export interface ScrollViewportLike {
  scrollHeight: number;
  clientHeight: number;
  scrollTop: number;
}

export interface ScrollAnchorTriple {
  anchorKey: string;
  anchorTop: number;
  scrollTop: number;
}

/** 触底判据（照 DSH：`floor − top <= threshold`；floor＝scrollHeight − clientHeight）。 */
export function nearBottom(el: ScrollViewportLike, threshold: number = FOLLOW_THRESHOLD_PX): boolean {
  return el.scrollHeight - el.clientHeight - el.scrollTop <= threshold;
}

/** 归属判据（照 DSH `movedByReader`）：差 > 0.5px 才算读者移动（`min` 防「布局增长／收缩钳制」被误判）。 */
export function movedByReader(top: number, ackTop: number, floor: number): boolean {
  return Math.abs(top - Math.min(ackTop, floor)) > READER_MOVE_EPSILON_PX;
}

/** 打断动画的阅读手势（照 DSH：wheel／touchstart／pointerdown；composer 内事件不算阅读手势）。 */
const GESTURE_EVENTS = ["wheel", "touchstart", "pointerdown"] as const;
/** keydown 只认滚动键（照 DSH；输入按键不算阅读手势）。 */
const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "]);

/** 逐会话锚点：会话内＝内存 Map；跨刷新＝localStorage（`xg:scroll:<sessionKey>`）。 */
const anchorMemo = new Map<string, ScrollAnchorTriple>();

function readScrollAnchor(sessionKey: string): ScrollAnchorTriple | null {
  const memo = anchorMemo.get(sessionKey);
  if (memo) return memo;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const raw = window.localStorage.getItem("xg:scroll:" + sessionKey);
      if (raw) {
        const parsed = JSON.parse(raw) as ScrollAnchorTriple;
        if (parsed && typeof parsed.scrollTop === "number") return parsed;
      }
    }
  } catch {
    /* 存储不可用（隐私模式等）：静默降级为「无锚点」——不假造位置 */
  }
  return null;
}

function saveScrollAnchor(sessionKey: string, triple: ScrollAnchorTriple): void {
  anchorMemo.set(sessionKey, triple);
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem("xg:scroll:" + sessionKey, JSON.stringify(triple));
    }
  } catch {
    /* 同上：降级不抛 */
  }
}

/** capture 口径（照 DSH）：视口顶 +1px 取首个非 `turn-process` 的 `data-chat-anchor-key` 锚点行。 */
function captureTriple(node: HTMLElement): ScrollAnchorTriple {
  const nodeTop = node.getBoundingClientRect().top;
  const rows = node.querySelectorAll<HTMLElement>("[data-chat-anchor-key]");
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    if (r.closest(".xg-turn-process")) continue;
    const anchorTop = r.getBoundingClientRect().top - nodeTop;
    if (anchorTop >= 1) {
      return {
        anchorKey: r.getAttribute("data-chat-anchor-key") ?? "",
        anchorTop,
        scrollTop: node.scrollTop,
      };
    }
  }
  return { anchorKey: "", anchorTop: 0, scrollTop: node.scrollTop };
}

export interface ChatFollowOptions {
  /** 会话键（G-2⑤ 逐会话存；缺省 "default"） */
  sessionKey?: string;
  /** 外层阈值（缺省 FOLLOW_THRESHOLD_PX＝25） */
  threshold?: number;
}

export interface ChatFollowApi {
  /** 跟随意图（true＝跟尾；外层写位走 **instant**） */
  followingTail: boolean;
  /** 待结算的读者移动（两量口径之第二量） */
  pending: boolean;
  /** 立即贴底（instant；只恢复外层——「回到底部」钮语义） */
  jumpToBottom: () => void;
  /** 记取当前锚点三元组（逐会话保存） */
  captureAnchor: () => void;
  /** 恢复锚点：先锚点行、行缺才退 raw `scrollTop` */
  restoreAnchor: () => void;
}

export function useChatFollow(
  targetRef: { current: HTMLElement | null },
  options: ChatFollowOptions = {},
): ChatFollowApi {
  const { sessionKey = "default", threshold = FOLLOW_THRESHOLD_PX } = options;
  const [followingTail, setFollowingTail] = useState(true);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const ackTopRef = useRef(0);
  const followingTailRef = useRef(followingTail);

  useEffect(() => {
    followingTailRef.current = followingTail;
  }, [followingTail]);

  const setPendingBoth = useCallback((v: boolean): void => {
    pendingRef.current = v;
    setPending(v);
  }, []);

  /** 结算（采样点／scrollend 先到者生效）：两支即时分支照 DSH。 */
  const settle = useCallback((): void => {
    const node = targetRef.current;
    if (!node) return;
    const top = node.scrollTop;
    const floor = node.scrollHeight - node.clientHeight;
    const userMoved = pendingRef.current;
    if (!userMoved && nearBottom(node, threshold)) {
      // 即时结算①：无读者移动的贴底 ⇒ 维持跟尾
      ackTopRef.current = top;
      if (!followingTailRef.current) setFollowingTail(true);
      return;
    }
    if (userMoved && top >= floor - READER_MOVE_EPSILON_PX) {
      // 即时结算②：读者真到 floor ⇒ 立即认账（恢复跟尾）
      setPendingBoth(false);
      ackTopRef.current = top;
      setFollowingTail(true);
      return;
    }
    if (userMoved && movedByReader(top, ackTopRef.current, floor)) {
      // 读者真移动且未贴底 ⇒ 脱离（等待「回到底部」）
      setPendingBoth(false);
      setFollowingTail(false);
    }
  }, [targetRef, threshold, setPendingBoth]);

  const jumpToBottom = useCallback((): void => {
    const node = targetRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;              // 外层 instant（禁 smooth——组内才 smooth）
    ackTopRef.current = node.scrollTop;
    setPendingBoth(false);
    setFollowingTail(true);
  }, [targetRef, setPendingBoth]);

  const captureAnchor = useCallback((): void => {
    const node = targetRef.current;
    if (!node) return;
    saveScrollAnchor(sessionKey, captureTriple(node));
  }, [targetRef, sessionKey]);

  const restoreAnchor = useCallback((): void => {
    const node = targetRef.current;
    if (!node) return;
    const saved = readScrollAnchor(sessionKey);
    if (!saved) return;
    if (saved.anchorKey) {
      const el = node.querySelector<HTMLElement>(
        '[data-chat-anchor-key="' + saved.anchorKey + '"]',
      );
      if (el) {
        const delta = (el.getBoundingClientRect().top - node.getBoundingClientRect().top) - saved.anchorTop;
        node.scrollTop += delta;                     // 锚点行相对偏移不变（±0.5px）
        ackTopRef.current = node.scrollTop;
        return;
      }
    }
    node.scrollTop = saved.scrollTop;                // 行缺 ⇒ 退 raw scrollTop
    ackTopRef.current = node.scrollTop;
  }, [targetRef, sessionKey]);

  /** 换会话：先保存旧会话锚点、再恢复新会话（G-2⑤/X32 逐会话位）。 */
  useEffect(() => {
    setFollowingTail(true);
    setPendingBoth(false);
    ackTopRef.current = 0;
    restoreAnchor();
    return () => {
      captureAnchor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  /** 事件接线：手势（打断）／scroll（跟尾即时）／scrollend（结算）／500ms 采样（结算）。 */
  useEffect(() => {
    const node = targetRef.current;
    if (!node) return undefined;

    const inComposer = (ev: Event): boolean => {
      const t = ev.target as Element | null;
      return !!(t && typeof t.closest === "function" && t.closest(".xg-composer"));
    };
    const onGesture = (ev: Event): void => {
      if (inComposer(ev)) return;                    // composer 内事件不算阅读手势（G-2③）
      setPendingBoth(true);                          // 读者移动 ⇒ pending（待采样/scrollend 结算）
    };
    const onKeydown = (ev: KeyboardEvent): void => {
      if (inComposer(ev)) return;
      if (!SCROLL_KEYS.has(ev.key)) return;          // 只认滚动键
      setPendingBoth(true);
    };
    const onScroll = (): void => {
      if (!followingTailRef.current || pendingRef.current) return;
      if (nearBottom(node, threshold)) {
        node.scrollTop = node.scrollHeight;          // 跟尾态贴底（instant）
        ackTopRef.current = node.scrollTop;
      }
    };
    const onScrollEnd = (): void => {
      settle();
    };

    GESTURE_EVENTS.forEach((name) => node.addEventListener(name, onGesture, { passive: true }));
    node.addEventListener("keydown", onKeydown as EventListener, true);
    node.addEventListener("scroll", onScroll, { passive: true });
    node.addEventListener("scrollend", onScrollEnd);
    const timer = window.setInterval(settle, SCROLL_SAMPLE_INTERVAL_MS);
    return () => {
      GESTURE_EVENTS.forEach((name) => node.removeEventListener(name, onGesture));
      node.removeEventListener("keydown", onKeydown as EventListener, true);
      node.removeEventListener("scroll", onScroll);
      node.removeEventListener("scrollend", onScrollEnd);
      window.clearInterval(timer);
    };
  }, [targetRef, threshold, settle, setPendingBoth]);

  return { followingTail, pending, jumpToBottom, captureAnchor, restoreAnchor };
}
